import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, RefreshCw, Upload } from 'lucide-react';
import { Button } from '../ui/button';

const rowsFrom = (payload) => Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
const errorText = (error, fallback) => error?.response?.data?.detail || error?.message || fallback;
const sizeLabel = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  return value < 1024 * 1024 ? `${Math.ceil(value / 1024)} KB` : `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const KnowledgeFilesPanel = ({ apiBase, authConfig, enabled }) => {
  const [items, setItems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/knowledge/files`, authConfig());
      setItems(rowsFrom(response.data));
    } catch (requestError) {
      if (requestError.response?.status === 404) setItems([]);
      else setError(errorText(requestError, 'Could not load knowledge files from Meta.'));
    } finally {
      setLoading(false);
    }
  }, [apiBase, authConfig, enabled]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!selectedFile) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const form = new FormData();
      form.append('file', selectedFile);
      await axios.post(`${apiBase}/knowledge/files`, form, {
        ...authConfig(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSelectedFile(null);
      setNotice('Knowledge file uploaded to Meta. It may take a moment to be indexed.');
      await load();
    } catch (requestError) {
      setError(errorText(requestError, 'Could not upload the knowledge file.'));
    } finally {
      setWorking(false);
    }
  };

  if (!enabled) {
    return <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">Configure the active WhatsApp number and server token before managing Agent Knowledge.</div>;
  }

  return <section className="space-y-5 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-5 sm:p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex gap-3">
        <div className="rounded-xl bg-indigo-500/15 p-2.5 text-indigo-400"><FileText className="h-5 w-5" /></div>
        <div><h2 className="text-xl font-semibold">Knowledge files</h2><p className="text-sm text-[var(--tg-text-muted)]">Upload durable source material for the agent: policies, programme information, and support documents.</p></div>
      </div>
      <Button data-tour="files-refresh" variant="outline" onClick={load} disabled={loading || working}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
    </div>
    <div data-tour="files-upload" className="rounded-xl border border-dashed border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input type="file" accept=".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} className="block w-full text-sm text-[var(--tg-text-secondary)] file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-500/15 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-300" />
        <Button onClick={upload} disabled={!selectedFile || working}>{working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload</Button>
      </div>
      <p className="mt-2 text-xs text-[var(--tg-text-muted)]">PDF, TXT, CSV, DOC/DOCX, XLS/XLSX up to 20 MB. Do not upload secrets or customer exports.</p>
      <p className="mt-1 text-xs text-[var(--tg-text-muted)]">Meta currently does not allow uploaded knowledge files to be removed through this API.</p>
    </div>
    {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
    {notice && <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{notice}</div>}
    <div data-tour="files-list" className="space-y-2">
      {loading ? <div className="flex justify-center py-16 text-[var(--tg-text-muted)]"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading files...</div> : items.map((item) => <div key={item.id || item.file_id || item.file_name || item.name} className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] p-3"><div className="min-w-0"><p className="truncate font-medium">{item.file_name || item.name || item.filename || 'Knowledge file'}</p><p className="mt-1 text-xs text-[var(--tg-text-muted)]">{item.status || item.processing_status || 'Available'}{sizeLabel(item.size || item.size_bytes) ? ` · ${sizeLabel(item.size || item.size_bytes)}` : ''}</p></div></div>)}
      {!loading && !items.length && <div className="rounded-xl border border-dashed border-[var(--tg-border-soft)] py-14 text-center text-sm text-[var(--tg-text-muted)]">No knowledge files uploaded yet.</div>}
    </div>
  </section>;
};

export default KnowledgeFilesPanel;
